import { useCallback, useEffect, useMemo, useState } from 'react'

import { useSearchParams } from 'react-router-dom'

export interface IListUrlStateFilter {
  key: string
  default: string
}

export interface IListUrlStateConfig {
  filters?: IListUrlStateFilter[]
  searchEnabled?: boolean
  searchKey?: string
  searchDebounceMs?: number
}

export interface IListUrlState {
  page: number
  searchQuery: string
  debouncedSearchQuery: string
  filters: Record<string, string>
  setPage: (page: number) => void
  setSearchQuery: (value: string) => void
  setFilter: (key: string, value: string) => void
}

const PAGE_PARAM = 'page'
const DEFAULT_SEARCH_KEY = 'search'
const DEFAULT_SEARCH_DEBOUNCE_MS = 500

const parsePage = (value: string | null): number => {
  if (value === null) {
    return 1
  }
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1
  }
  return parsed
}

export const useListUrlState = (config: IListUrlStateConfig): IListUrlState => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filterConfigs = config.filters ?? []
  const searchEnabled = config.searchEnabled ?? false
  const searchKey = config.searchKey ?? DEFAULT_SEARCH_KEY
  const searchDebounceMs = config.searchDebounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS

  // Stable signature of the filter config — survives inline-array callers.
  const filterConfigsKey = filterConfigs
    .map(f => `${f.key}\0${f.default}`)
    .join('\0')

  const page = parsePage(searchParams.get(PAGE_PARAM))

  const filters = useMemo<Record<string, string>>(() => {
    const result: Record<string, string> = {}
    for (const filter of filterConfigs) {
      result[filter.key] = searchParams.get(filter.key) ?? filter.default
    }
    return result
    // filterConfigs is intentionally compared by filterConfigsKey (not by ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, filterConfigsKey])

  const debouncedSearchQuery = searchEnabled
    ? (searchParams.get(searchKey) ?? '')
    : ''

  // Local raw search state — seeded from URL on mount so the input is responsive
  // while the debounce is pending.
  const [searchQuery, setSearchQueryRaw] =
    useState<string>(debouncedSearchQuery)

  // When the URL's search value changes externally (back/forward, manual edit),
  // re-sync the raw state.
  useEffect((): void => {
    if (searchEnabled) {
      setSearchQueryRaw(debouncedSearchQuery)
    }
  }, [debouncedSearchQuery, searchEnabled])

  // Debounce raw → URL writes. Cleanup clears any pending timer on unmount or
  // when searchQuery changes again before the timeout fires.
  useEffect((): (() => void) | void => {
    if (!searchEnabled) {
      return
    }
    if (searchQuery === debouncedSearchQuery) {
      return
    }
    const timeoutId = window.setTimeout((): void => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          if (searchQuery === '') {
            params.delete(searchKey)
          } else {
            params.set(searchKey, searchQuery)
          }
          params.delete(PAGE_PARAM)
          return params
        },
        { replace: true },
      )
    }, searchDebounceMs)

    return (): void => {
      window.clearTimeout(timeoutId)
    }
  }, [
    searchQuery,
    debouncedSearchQuery,
    searchEnabled,
    searchKey,
    searchDebounceMs,
    setSearchParams,
  ])

  const setPage = useCallback(
    (next: number): void => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          if (next <= 1) {
            params.delete(PAGE_PARAM)
          } else {
            params.set(PAGE_PARAM, String(next))
          }
          return params
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const setFilter = useCallback(
    (key: string, value: string): void => {
      // Read default from the latest filterConfigs at call time (captured via closure).
      const filter = filterConfigs.find(f => f.key === key)
      const defaultValue = filter?.default ?? ''
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev)
          if (value === defaultValue) {
            params.delete(key)
          } else {
            params.set(key, value)
          }
          params.delete(PAGE_PARAM)
          return params
        },
        { replace: true },
      )
    },
    // Stable by the serialized key, not the inline-array ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams, filterConfigsKey],
  )

  const setSearchQuery = useCallback((value: string): void => {
    setSearchQueryRaw(value)
  }, [])

  return {
    page,
    searchQuery: searchEnabled ? searchQuery : '',
    debouncedSearchQuery,
    filters,
    setPage,
    setSearchQuery,
    setFilter,
  }
}
